import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelectedPlanIncludesCertificates } from '../../prcng-1194_v1_mc_pro_tier/hooks/useSelectedPlanIncludesCertificates'
import { useCertificateCreditAddOn } from '../hooks/useCertificateCreditAddOn'
import { useExperimentCriteria } from '../hooks/useExperimentCriteria'

type CertsAddOnValue = {
  isCardVisible: boolean
  isAdded: boolean
  // Whether this visitor came in off-bucket from a campaign link, which
  // Continue has to record on the URL for the payment step (PRCNG-1193). Read
  // from the param, not from the current selection, so it survives a deselect
  // and re-select.
  isCampaign: boolean
  isLoading: boolean
  price: string
  savings: string
  productId: number
  productType: string
  toggle: () => void
  monthlyPrice: string
  discount?: number
}

const CertsAddOnContext = createContext<CertsAddOnValue | undefined>(undefined)

/**
 * Holds the card's selection so the plans CTA can label itself from it. The
 * selection is local to the page — nothing is written to the cart here
 * (PRCNG-1150).
 */
export const CertsAddOnProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { isEligible, isPreselected, isCampaign } = useExperimentCriteria()
  const isCertificateIncluded = useSelectedPlanIncludesCertificates()
  const [isSelected, setIsSelected] = useState(false)
  const hasSeeded = useRef(false)

  const {
    monthlyPrice,
    discount,
    productId,
    productType,
    isLoading,
    savings,
    isUnavailable,
    price,
  } = useCertificateCreditAddOn({ enabled: isEligible })

  const isOfferable = isEligible && !isUnavailable
  const isCardVisible = isOfferable && !isCertificateIncluded

  // The card is invisible on first render — the param is unread and the price
  // has not arrived — so a campaign visitor's selection has to be seeded when
  // it becomes visible, not at mount. Seeded once: losing the price still
  // clears the selection, and it must not come back over a later deselection.
  useEffect(() => {
    if (!isOfferable) {
      setIsSelected(false)
      return
    }

    if (hasSeeded.current) return

    hasSeeded.current = true
    setIsSelected(isPreselected)
  }, [isOfferable, isPreselected])

  const value = useMemo(
    () => ({
      isCardVisible,
      // A selection made before the price disappeared must not survive it, or
      // the CTA would promise a certificate the card no longer offers. Hiding
      // the card for such a plan keeps the selection, so it returns with the card.
      isAdded: isCardVisible && isSelected,
      isCampaign,
      isLoading,
      price,
      monthlyPrice,
      discount,
      savings,
      productId,
      productType,
      toggle: () => setIsSelected((wasSelected) => !wasSelected),
    }),
    [
      isCardVisible,
      isSelected,
      isCampaign,
      isLoading,
      monthlyPrice,
      discount,
      savings,
      productId,
      productType,
      price,
    ],
  )

  return (
    <CertsAddOnContext.Provider value={value}>
      {children}
    </CertsAddOnContext.Provider>
  )
}

export const useCertsAddOn = () => {
  const ctx = useContext(CertsAddOnContext)

  if (!ctx) {
    throw new Error('useCertsAddOn was used outside of CertsAddOnProvider')
  }

  return ctx
}
